import { KTCard, KTCardBody, KTIcon } from "../../../_metronic/helpers";
import { useApiIsLoaded, useMapsLibrary } from '@vis.gl/react-google-maps'
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import MapContainer from "./MapContainer";
import { useAuth } from "../../../app/modules/auth";

const LISTING_STATUS = {
    DELETED: -1,
    DRAFT: 0,
    ACTIVE: 1,
    INACTIVE: 2,
    HOLD: 3,
    PENDING_APPROVAL: 4,
    APPROVAL_REJECTED: 5,
}

const getMarkerColor = (status, isZero) => {
    if (isZero && [1, 2, 3]?.includes(status)) {
        return [255, 64, 61]
    }

    switch (status) {
        case LISTING_STATUS.ACTIVE:
            return [255, 184, 0]
        case LISTING_STATUS.PENDING_APPROVAL:
            return [76, 175, 80]
        case LISTING_STATUS.DRAFT:
            return [0, 0, 0]
        case LISTING_STATUS.INACTIVE:
            return [33, 150, 243]
        case LISTING_STATUS.HOLD:
            return [156, 39, 176]
        default:
            return [0, 0, 0]
    }
}

const ViewMap = () => {
    const { currentUser, haveAccess } = useAuth()
    const apiIsLoaded = useApiIsLoaded()
    const placesLib = useMapsLibrary('places')
    const inputRef = useRef(null)
    const [isSearch, setIsSearch] = useState(false)
    const [search, setSearch] = useState({ lat: 38.6311966, lng: -98.1633745 })
    const [placeAutocomplete, setPlaceAutocomplete] = useState(null)
    const [mapLoaded, setMapLoaded] = useState(false)
    const [markers, setMarkers] = useState([])
    const [filteredMarkers, setFilteredMarkers] = useState([])
    const [markersLoaded, setMarkersLoaded] = useState(false)
    const [filters, setFilters] = useState([
        { id: 'active', label: 'Active', checked: true, color: '#ffb800ff', status: [1], total: 0 },
        { id: 'pending', label: 'Pending', checked: true, color: '#4caf50ff', status: [4], total: 0 },
        { id: 'draft', label: 'Draft', checked: true, color: '#000000', status: [0, 5], total: 0 },
        { id: 'holdByUser', label: 'On Hold by User', checked: true, color: '#0396f3ff', status: [2], total: 0 },
        { id: 'holdByAdmin', label: 'On Hold by Admin', checked: true, color: '#9c27b0ff', status: [3], total: 0 },
        { id: 'zero', label: 'Zero Seats', checked: true, color: '#ff403dff', total: 0 },
    ])

    useEffect(() => {
        if (!apiIsLoaded) {
            setMapLoaded(false)
            setMarkersLoaded(false)
            setMarkers([])
        } else {
            setMapLoaded(true)
        }
    }, [apiIsLoaded])

    const filterMarkers = (markers, filters) => {
        const zeroFilter = filters.find(f => f.id === 'zero');
        const zeroSensitiveStatuses = [1, 2, 3];

        return markers.filter(m => {
            if (zeroSensitiveStatuses.includes(m.status)) {
                if (m.is_zero) {
                    return zeroFilter?.checked
                }
            }

            return filters.every(f => {
                if (f.id === 'zero') return true;
                if (!f.checked) {
                    return !f.status.includes(m.status);
                }
                return true;
            });
        });
    };

    useEffect(() => {
        if (markers.length) {
            setFilteredMarkers(filterMarkers(markers, filters))
        }
    }, [markers, filters])

    useEffect(() => {
        setMarkersLoaded(false)
        if (apiIsLoaded) {
            axios.get(`${process.env.REACT_APP_API_V3_URL}/listing/listings-map`)
                .then((res) => {
                    const markers = res.data
                    setMarkers(markers?.map((el) => {
                        return {
                            ...el,
                            lat: +el.lat,
                            lng: +el.lng,
                            geometry: {
                                type: 'Point',
                                coordinates: [+el.lng, +el.lat],
                            },
                            properties: {},
                            coordinates: [+el.lng, +el.lat],
                            color: getMarkerColor(el.status, el.is_zero),
                            link: `/listings/${el.id}`
                        }
                    }))
                    setFilters((prev) => prev.map((f) => {
                        if (f.id === 'zero') {
                            return { ...f, total: res.data?.filter(m => m.is_zero && [1, 2, 3].includes(m.status)).length };
                        }
                        return { ...f, total: res.data?.filter(m => !m.is_zero && f.status.includes(m.status)).length };
                    }));
                    setMarkersLoaded(true)
                })
                .catch((err) => {
                    console.error('Error fetching markers:', err)
                    setMarkersLoaded(true)
                })
        }
    }, [apiIsLoaded])

    const getPlaceViewport = (place) => {
        if (place.geometry && place.geometry.viewport) {
            return place.geometry.viewport
        }

        if (place.geometry && place.geometry.location) {
            try {
                const location = place.geometry.location
                const buffer = 0.01
                return new window.google.maps.LatLngBounds(
                    new window.google.maps.LatLng(location.lat() - buffer, location.lng() - buffer),
                    new window.google.maps.LatLng(location.lat() + buffer, location.lng() + buffer)
                )
            } catch (err) {
                console.error(err)
            }
        }

        return null
    }

    const getSearchedPlace = (place) => {
        try {
            document.body.className = ''
            let n, e, s, w
            const viewport = getPlaceViewport(place)
            if (viewport) {
                n = viewport?.getNorthEast()?.lat()
                e = viewport?.getNorthEast()?.lng()
                s = viewport?.getSouthWest()?.lat()
                w = viewport?.getSouthWest()?.lng()
            }

            const lat = place?.geometry?.location?.lat()?.toFixed(6) || 38.6311966
            const lng = place?.geometry?.location?.lng()?.toFixed(6) || -98.1633745
            setSearch({ lat: +lat, lng: +lng })
            setIsSearch(true)
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        if (!placesLib || !inputRef.current) return

        const options = {
            fields: ['geometry', 'name', 'formatted_address'],
            componentRestrictions: { country: 'us' }
        }
        setPlaceAutocomplete(new placesLib.Autocomplete(inputRef.current, options))
    }, [placesLib, inputRef.current])


    useEffect(() => {
        if (!placeAutocomplete) return

        placeAutocomplete.addListener('place_changed', async () => {
            const place = placeAutocomplete.getPlace()
            if (place && place.geometry) {
                getSearchedPlace(place)
            }
        })
    }, [placeAutocomplete])

    return (
        <>
            {haveAccess(currentUser, 'listing_map') ?
                (
                    <KTCard>
                        <KTCardBody>
                            <div className="d-flex align-items-center position-relative mb-5">
                                <KTIcon
                                    iconName="magnifier"
                                    className="fs-1 position-absolute ms-6"
                                />
                                <input
                                    type="text"
                                    data-kt-user-table-filter="search"
                                    className="form-control form-control-solid w-220px ps-14"
                                    placeholder={'Search Spaces...'}
                                    style={{ textTransform: 'capitalize' }}
                                    disabled={!mapLoaded}
                                    ref={inputRef}
                                    name='search'
                                    id='search'
                                />
                            </div>
                            <div className="row g-10">
                                <div className="col-md-3">
                                    {/* filters */}
                                    <div className="px-5 py-5 border border-gray-300 bg-gray-100 rounded">
                                        <h4 className="fw-bold fs-4 mb-3">Filters:</h4>
                                        <div className="d-grid gap-3">
                                            {
                                                filters.map((item, index) => (
                                                    <div className="d-flex gap-2 align-items-center flex-wrap" key={index}>
                                                        <div className="w-15px h-15px" style={{ backgroundColor: item.color }} />
                                                        <div style={{ display: "flex", alignItems: 'center', columnGap: 8 }}>
                                                            <input
                                                                className="form-check-input custom-checkbox"
                                                                type="checkbox"
                                                                value={item.checked}
                                                                checked={item.checked}
                                                                onChange={() => {
                                                                    setFilters(prev => prev.map(el => {
                                                                        if (el.id === item.id) {
                                                                            return {
                                                                                ...el,
                                                                                checked: !el.checked
                                                                            }
                                                                        }
                                                                        return el
                                                                    }))
                                                                    setFilteredMarkers(markers)
                                                                }}
                                                            />
                                                            <div className="fw-bold">{item.label} ({item.total})</div>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                    {/* listings */}
                                    {/* <div className="p-3 border border-gray-300 bg-gray-100 rounded mt-5">
                            <h3 className="fw-bold fs-4 mb-5">Listings</h3>
                            {
                                filteredMarkers?.map((item, index) => (
                                    <div className="d-flex gap-2 align-items-center flex-wrap" key={index}>
                                        <div className="w-15px h-15px" style={{ backgroundColor: item.color }} />
                                        <div className="fw-bold">{item.title}</div>
                                    </div>
                                ))
                            }
                        </div> */}
                                </div>
                                <div style={{ flex: 1, width: '100%', height: 'calc(100vh - 300px)', position: 'relative' }}>
                                    {mapLoaded && (
                                        <MapContainer
                                            markers={filteredMarkers}
                                            markersLoaded={markersLoaded}
                                            mapLoaded={mapLoaded}
                                            center={search}
                                            isSearch={isSearch}
                                        />
                                    )}
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>
                )
                :
                <div className='d-flex text-center w-100 align-content-center justify-content-center' style={{ height: '70vh', alignItems: 'center', fontSize: '42px', color: '#ffa049', background: 'rgba(0,0,0,0.1)' }}>
                    You do not have permission to view
                </div>
            }
        </>
    );
};

export default ViewMap;

